import { type ActionPointConfigDto, ConditionOperator, type DataType, type DeviceAttributeEntity, DeviceAttributeType, type DeviceType as ApiDeviceType, type DeviceUptimeStatus, type IncidentRule, type IncidentRuleStatus, type LayoutDto, type MeasurementUnit, type ScheduleConfig, type ScheduleConfigDto, type WorkflowValueValidationRule } from '@api/devices';
import { type IncidentDto, type IncidentSeverity } from '@api/incidents';
import { EntityStatus } from '@core';
import { type Communicator, type Firmware, type RemoteAction } from '@devices';
import { type LocationAddress, type Space } from '@locations';

// TODO: fix relative imports

export { ConditionOperator, DeviceAttributeType, type MeasurementUnit, type ScheduleConfig, type ScheduleConfigDto };
export type DeviceAttribute = DeviceAttributeEntity;

export enum MeasurementUnitShort {
  W = ' W',
  MBPS = 'Mbps',
  COUNT = 'count',
  YN = 'Y/N',
  KWH = 'kWh',
  A = 'A',
}

export enum DeviceTableCols {
  STATUS = 'status',
  BUILDING = 'building',
  SELECT = 'select',
  INDEX = 'index',
  DEVICE_NAME = 'deviceName',
  ACTIONS = 'actions',
  MANUFACTURER = 'manufacturer',
  MODEL = 'model',
  SERIAL_NUMBER = 'serialNumber',
  DEVICE_TYPE = 'deviceType',
  INCIDENTS = 'incidents',
  FIRMWARE = 'firmware',
  MAC_ADDRESS = 'mac address',
  FLOOR = 'floor',
  ROOM = 'room',
  IP_ADDRESS = 'ipAddress',
  LAST_REPORTED = 'latestStreamingTimestamp',
  IS_DC = 'isDC',
}

export const ADMIN_DEVICE_TABLE_COLS = [
  DeviceTableCols.IS_DC,
];

export enum DeviceRelationType {
  PARENT_CHILD = 'parent_child',
  DC_DM = 'dc_dm',
}

export const DeviceStatusActions = {
  [EntityStatus.Active]: 'activate',
  [EntityStatus.Paused]: 'pause',
  [EntityStatus.Archived]: 'archive',
  [EntityStatus.Deactivated]: 'deactivate',
  [EntityStatus.Unplugged]: 'unplugged',
};


export interface DeviceData {
  actionPoints?: ModelActionPoint[];
  additionalAttributes?: DeviceAdditionalAttribute[];
  alertStatus: DeviceAlertStatus;
  appKey: null | string;
  // contains only directly assigned devices, excluding ports, computed property
  childRelations?: ChildRelationDevices[];
  communicator?: Communicator;
  createdOn: string;
  description?: string;
  devEUI: null | string;
  deviceModelInformation?: DeviceModel;
  deviceProfileId?: string;
  deviceRemoteActions?: RemoteAction[];
  friendlyName: string;
  gatewayId: null | string;
  hyperlink?: string;
  id: string;
  importantDates?: {
    comissioningInfo: {
      dateTime: string;
    };
    decomissionedInfo: null | string;
    installationInfo: {
      dateTime: string;
    };
    provisioningInfo: null | string;
  };
  incidentCountBySeverity: Record<IncidentSeverity, number>;
  incidentRules?: IncidentRuleCommon[];
  incidents: IncidentDto[];
  integrationDeviceId: null | string;
  isDC: boolean;
  latestFirmware: Partial<Firmware> | null;
  latestStreamingTimestamp?: string;
  latestUptimeStatusChangeTimestamp?: null | string;
  location: DeviceLocationData;
  locationId?: string;
  name: string,
  outletDevices?: ChildRelationDevices[],
  outlets: number | null;
  parentDevice?: {
    friendlyName: string,
    id: string,
    name: string,
    orderNumber: number,
    status: EntityStatus;
  };
  parentRelations?: ParentRelationDevice[];
  parentSpaceId?: string,
  portDevices?: ChildRelationDevices[],
  ports: number | null;
  spacePath?: Space[];
  status: EntityStatus;
  unstructuredDataReference: string
  uptimeStatus?: DeviceUptimeStatus | null;
}

export interface ChildRelationDevices {
  childDevice: ChildDevice;
  id: string;
  orderNumber: number;
  relationType: DeviceRelationType,
}

export interface ParentRelationDevice {
  id: string;
  orderNumber: number;
  parentDevice: ParentDevice;
  relationType: DeviceRelationType,
}

export interface ChildDevice {
  additionalAttributes: DeviceAdditionalAttribute[];
  deviceTypeId: string;
  id: string;
  incidentCountBySeverity: Record<IncidentSeverity, number>;
  measurementUnit?: MeasurementUnit;
  name: string;
  status: EntityStatus;
}

export interface ParentDevice {
  id: string;
  name: string;
  status: EntityStatus;
}

export interface NormalizedPortTableItem {
  childDevice: {
    activity: {
      rx: string,
      tx: string,
    };
    additionalAttributes: {
      [key: string]: string;
    }[],
    id: string;
    incidentCountBySeverity: Record<IncidentSeverity, number>;
    name: string;
    status: EntityStatus;
  };
  id: string;
  orderNumber: number;
}

export interface ExtendedDeviceData extends DeviceData {
  position: null | Record<string, any>;
}

export enum DeviceAlertStatus {
  IN_ALERT = 'inAlert',
  IN_WARNING = 'inWarning',
  OK = 'ok',
}

export interface DeviceLocationData {
  address: LocationAddress;
  clientFriendlyId: string;
  friendlyName: string;
  id: string;
  name: string;
  status: EntityStatus;
}

export interface DeviceAdditionalAttribute {
  attributeType: DeviceAttributeType;
  friendlyName: string;
  isManual: boolean;
  measurementUnit?: MeasurementUnit;
  name: string;
  recordedTimeStamp: string | null;
  timestamp?: string;
  value: string | null;
}

// TODO: refactor NormalizedDeviceData and DeviceData to be one consistent interface
export interface NormalizedDeviceData {
  additionalAttributes?: DeviceAdditionalAttribute[];
  building: string;
  buildingId: string;
  buildingStatus: string;
  deviceName: string;
  deviceType: string;
  deviceTypeId: string;
  firmware: string;
  floor: string;
  floorId: string;
  id: string;
  incidents: IncidentDto[];
  integrationDeviceId: string;
  ipAddress: string;
  isDC: boolean;
  latestFirmware: Partial<Firmware> | null;
  latestStreamingTimestamp: string | null;
  manufacturer: string;
  model: string;
  room: string;
  roomId: string;
  serialNumber: string;
  spacePath?: Space[];
  status: EntityStatus;
}

export interface DeviceChart {
  friendlyName: string;
  measurementUnit: null | MeasurementUnit;
  name: string;
  timeseries: { timeStamp: string; value: number; }[];
}


export interface PowerConsumptionAttribute {
  friendlyName: string,
  id: string,
  measurementUnitId: string,
  name: string,
  statistics: ConsumptionStatistics;
}

export interface ConsumptionStatistics {
  average: number,
  max: number,
  min: number,
  total: number,
}

export interface PowerConsumptionDevice extends NormalizedDeviceData {
  children: PowerConsumptionDevice[];
  deviceTotalPowerConsumption: number;
  measurementUnit: MeasurementUnit;
  ownAndChildPowerConsumptionStatistics: ConsumptionStatistics;
  ownPowerConsumptionStatistics: ConsumptionStatistics;
  powerConsumptionAttributes: PowerConsumptionAttribute[];
}

export interface PowerConsumptionResponse {
  count: number;
  devices: PowerConsumptionDevice[];
  totalConsumption: number;
}

export interface DeviceManufacturer {
  devicesCount?: string;
  id: string;
  modelsCount?: string;
  name: string;
}

export interface DeviceType extends ApiDeviceType {
  devicesCount?: string;
  modelsCount?: string;
}

export interface DeviceModel {
  devicesCount?: string;
  deviceType: string;
  documentReference: null | string;
  id: string;
  layout?: LayoutDto;
  make: DeviceManufacturer;
  name: string;
  outlets?: string;
  ownerClientId: string;
  ports?: string;
  type: DeviceType;
}

export interface DeviceFullModel extends DeviceModel {
  actionPoints: ModelActionPoint[];
  standardAttributes: DeviceAttribute[];
}

export interface NewDeviceModelData {
  actionPointsDto: ActionPointConfigDto[];
  deviceTypeId: DeviceModel['deviceType'];
  layout?: LayoutDto;
  logo?: string | null;
  makerId?: string;
  name: DeviceModel['name'];
  outlets?: string;
  ports?: string;
  standardAttributeIds: DeviceAttribute['id'][];
}

export interface FilterOption {
  parentId?: string;
  title: string;
  value: string;
}

export interface ActionPointBase {
  friendlyName?: string | null;
  id: string;
  name: string;
}

export interface ValueValidationRule extends Partial<WorkflowValueValidationRule> {
  config?: ActionPointValidationConfig;
}

export enum ActionPointValidationConfig {
  ENUM = 'enum',
  MIN_MAX = 'min-max',
}

export interface ModelActionPoint extends ActionPointBase {
  clientId: string;
  dcUid: null | string;
  deviceActionPointsId: string,
  valueDataType: DataType;
  valueValidationRule: ValueValidationRule | null;
}


export interface WorkflowBase {
  actionDeviceId?: string | null;
  actionPointId?: string | null;
  code: string; // datapoint name
  conditionValue: string;
  deviceId: string;
  maxAttempts: number;
  name: string;
  operator: ConditionOperator;
  remoteDeviceActionId?: string | null;
  remoteDeviceId?: string | null;
  scheduleConfig?: ScheduleConfigDto | null;
  scheduleEnabled?: boolean;
  timeoutBetweenAttempts: number;
  value?: string;
}

export interface Workflow extends WorkflowBase {
  actionDeviceName: string;
  actionPointName: string;
  clientId: string;
  createdAt: string;
  currentAttempts: number;
  deviceName: string;
  id: string;
  lastExecutedAt: string;
  status: WorkflowStatus;
}

export enum WorkflowStatus {
  ACTIVE = 'active',
  PAUSED = 'paused',
  ATTEMPTS_EXCEEDED = 'attempts_exceeded',
  DISABLED = 'disabled',
  ATTEMPT_RUNNING = 'attempt_running',
  TWO_WAY_COMMUNICATION_DISABLED = 'two_way_communication_disabled'
}

export interface IncidentRuleBase {
  code: string;
  conditionValue: string;
  name: string | null;
  operator: ConditionOperator;
  severity: IncidentSeverity;
  status: IncidentRuleStatus;
}

export interface IncidentRuleCommon extends Omit<IncidentRule, 'deviceId' | 'scheduleEnabled'> {
  enabled?: boolean;
  incidents?: IncidentDto[];
  scheduleEnabled?: boolean;
}

export interface DeviceIncidentRule extends IncidentRuleCommon {
  deviceId: string;
  deviceName?: string;
  friendlyName?: string;
}

export interface ModelIncidentRule extends IncidentRuleCommon {
  deviceModelId: string;
}

export enum PortLayout {
  ONE_ROW = 1,
  TWO_ROWS
}

export enum SortingType {
  CONSECUTIVE = 1,
  ALTERNATING
}
